import { defineAsyncComponent } from '../compat/vueCompat';
import type { ClientHookContextData } from '@mimir/common/dto/clientHooks';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { ViewType } from '@mimir/common/dto/mdfModels';
import type { CreateItemDialogRequest, CreateItemHandler } from './createItemDialogRequest';
import { requestDialog } from './requestDialog';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';

/**
 * Show a dialog to the user that offers them to create an item.
 *
 * @param folderId If passed, the item will be created in this folder.
 * @param actionCallback A callback executed when 'Create' is clicked. Will contain the target
 * folderId, metadata, mouse event and chosen item type (if available)
 * @param initialMetadata Metadata that is initially assigned to the item
 * @param showMediaTypeSelection Whether to show the media type selection dropdown
 * @param headerText Custom header text
 */
export function showCreateItemDialog(
  folderId: string | undefined,
  actionCallBack: CreateItemHandler,
  initialMetadata?: ItemMetaData,
  showMediaTypeSelection = true,
  headerText?: string,
  viewType?: ViewType,
  clientHookContextData?: ClientHookContextData,
  showVisibilitySettings = true,
  initVisibleTo?: ItemAccessControl,
  initUpdatableBy?: ItemAccessControl,
  initialItemType?: DynamoItemType,
) {
  const contextData: ClientHookContextData = clientHookContextData ?? {
    context: 'create_placeholder',
    itemType: undefined,
    finalization: undefined,
    initialization: undefined,
  };
  const req: CreateItemDialogRequest = {
    folderId,
    initialMetadata,
    createItemHandler: actionCallBack,
    showMediaTypeSelection,
    showVisibilitySettings,
    initVisibleTo,
    initUpdatableBy,
    headerText,
    viewType,
    clientHookContextData: contextData,
    initialItemType,
  };
  showDialogFromRequest(req);
}


export function showCreateSequenceDialog(
  createItemHandler: CreateItemHandler,
  sequenceType: 'clipList' | 'timeline',
) {
  const req: CreateItemDialogRequest = {
    headerText: 'Create a new Mimir sequence',
    createItemHandler,
    showMediaTypeSelection: false,
    showVisibilitySettings: true,
    showAnalytics: false,
    initialItemType: sequenceType,
    clientHookContextData: {
      context: 'create_placeholder',
      itemType: sequenceType,
      initialization: undefined,
      finalization: undefined,
    }
  };
  showDialogFromRequest(req);
}

export function showCreateGraphicsDialog(
  createItemHandler: CreateItemHandler,
) {
  const req: CreateItemDialogRequest = {
    headerText: 'Create a new Mimir graphics',
    createItemHandler,
    showMediaTypeSelection: false,
    showVisibilitySettings: true,
    showAnalytics: false,
    initialItemType: 'graphics',
    clientHookContextData: {
      context: 'create_graphics',
      itemType: 'graphics',
      initialization: undefined,
      finalization: undefined,
    }
  };
  showDialogFromRequest(req);
}

const CreateItemDialog = defineAsyncComponent(() => import('@mimir/web/components/Common/Dialogs/dialogs/CreateItemDialog.vue'));

function showDialogFromRequest(req: CreateItemDialogRequest) {
  requestDialog(req, CreateItemDialog);
}
